import { m } from 'framer-motion';
import { useState, useCallback, useEffect } from 'react';
// @mui
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import List from '@mui/material/List';
import Stack from '@mui/material/Stack';
import Badge from '@mui/material/Badge';
import Drawer from '@mui/material/Drawer';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
import { useResponsive } from 'src/hooks/use-responsive';
// _mock
import { _notifications } from 'src/_mock';
// components
import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { varHover } from 'src/components/animate';
import { useSnackbar } from 'src/components/snackbar';
//
import NotificationItem from './notification-item';
import { useGetNotificationsQuery, useMarkAsReadMutation } from 'src/store/Reducer/notification';
import { useLazyGetFilesQuery } from 'src/store/Reducer/file';
import FileManagerFileDetails from 'src/sections/file-manager/file-manager-file-details';


export default function NotificationsPopover() {
  const drawer = useBoolean();

  const details = useBoolean();

  const smUp = useResponsive('up', 'sm');

  const [markAllRead] = useMarkAsReadMutation();

  const { enqueueSnackbar } = useSnackbar();

  const [triggerGetFiles, { data: folderDetail, isLoading }] = useLazyGetFilesQuery();
  const { data, refetch } = useGetNotificationsQuery({ page: 1, limit: 500 },
    {
      pollingInterval: 60000,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    });


  useEffect(() => {
    if (data?.data?.length) {
      setNotifications(data?.data);
    }
  }, [data]);

  const [notifications, setNotifications] = useState(data);

  const totalUnRead = data?.total_unread || 0;

  const handleMarkAllAsRead = async () => {
    try {
      const data = { notification_ids: [] }
      await markAllRead(data).unwrap();
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, is_read: '1' }))
      );
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  const handleNotificationClick = async (notification) => {
    try {
      const { object_id, subject_id } = notification;
      if (object_id && subject_id) {
        details.onTrue();
        await triggerGetFiles({ category: object_id, folder: subject_id }).unwrap();
      }
    } catch (err) {
      console.error('Error fetching files for notification:', err);
      enqueueSnackbar(err?.data?.message || 'Failed to get file detial', {
        variant: 'error',
      });
    }

  };

  const renderHead = (
    <Stack direction="row" alignItems="center" sx={{ py: 2, pl: 2.5, pr: 1, minHeight: 68 }}>
      <Typography variant="h6" sx={{ flexGrow: 1 }}>
        Notifications
      </Typography>

      {!!totalUnRead && (
        <Tooltip title="Mark all as read">
          <IconButton color="primary" onClick={handleMarkAllAsRead}>
            <Iconify icon="eva:done-all-fill" />
          </IconButton>
        </Tooltip>
      )}

      {!smUp && (
        <IconButton onClick={drawer.onFalse}>
          <Iconify icon="mingcute:close-line" />
        </IconButton>
      )}
    </Stack>
  );

  const renderList = (
    <Scrollbar>
      <List disablePadding>
        {notifications?.length > 0 && notifications?.map((notification) => (
          <NotificationItem
            key={notification?.id}
            notification={notification}
            onClick={() => handleNotificationClick(notification)}
          />
        ))}
      </List>
    </Scrollbar>
  );

  return (
    <>
      <IconButton
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        color={drawer.value ? 'primary' : 'default'}
        onClick={drawer.onTrue}
      >

        <Badge badgeContent={totalUnRead} color="error">
          <Iconify icon="solar:bell-bing-bold-duotone" width={24} />
        </Badge>
      </IconButton>

      <Drawer
        open={drawer.value}
        onClose={drawer.onFalse}
        anchor="right"
        slotProps={{
          backdrop: { invisible: true },
        }}
        PaperProps={{
          sx: { width: 1, maxWidth: 420 },
        }}
      >
        {renderHead}
        <Divider />
        {renderList}
      </Drawer>

      <FileManagerFileDetails
        item={folderDetail?.data}
        isLoading={isLoading}
        open={details.value}
        onClose={details.onFalse}
        refetch={refetch}
      />
    </>
  );
}
